import { Stack, Typography } from '@mui/material'
import { DatePicker, type DateView } from '@mui/x-date-pickers'
import { type FC } from 'react'

const defaultSlotProps = {
    textField: {
        size: 'small' as const,
        sx: {
            flex: 1,
            '& .MuiOutlinedInput-root': {
                fontSize: '0.75rem',
                py: 0,
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'text.secondary' },
            },
            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
        },
    },
    openPickerButton: { sx: { p: 0.25 } },
    openPickerIcon: { sx: { fontSize: '0.875rem' } },
}

const errorSlotProps = {
    ...defaultSlotProps,
    textField: {
        ...defaultSlotProps.textField,
        error: true,
    },
}

interface DateRangeInputProps {
    from: Date | null
    to: Date | null
    onFromChange: (date: Date | null) => void
    onToChange: (date: Date | null) => void
    format?: string
    views?: DateView[]
    fromLabel?: string
    toLabel?: string
    maxDate?: Date
    disableOpenPicker?: boolean
}

export const DateRangeInput: FC<DateRangeInputProps> = ({
    from,
    to,
    onFromChange,
    onToChange,
    format = 'MM/yyyy',
    views = ['year', 'month'],
    fromLabel = 'From',
    toLabel = 'To',
    maxDate,
    disableOpenPicker,
}) => {
    const handleChange =
        (setter: (date: Date | null) => void) => (date: Date | null) => {
            setter(date && !isNaN(date.getTime()) ? date : null)
        }

    const yearOnly = views.length === 1 && views[0] === 'year'
    const hasRangeError = from && to && from > to
    const baseSlotProps = hasRangeError ? errorSlotProps : defaultSlotProps
    const slotProps = yearOnly
        ? {
              ...baseSlotProps,
              layout: { sx: { '.MuiDateCalendar-root': { height: 'auto' } } },
          }
        : baseSlotProps

    return (
        <Stack gap={0.5}>
            <Stack direction="row" gap={1}>
                <DatePicker
                    label={fromLabel}
                    views={views}
                    format={format}
                    value={from}
                    onChange={handleChange(onFromChange)}
                    maxDate={maxDate}
                    disableOpenPicker={disableOpenPicker}
                    slotProps={slotProps}
                />
                <DatePicker
                    label={toLabel}
                    views={views}
                    format={format}
                    value={to}
                    onChange={handleChange(onToChange)}
                    maxDate={maxDate}
                    disableOpenPicker={disableOpenPicker}
                    slotProps={slotProps}
                />
            </Stack>
            {hasRangeError && (
                <Typography
                    variant="body2"
                    color="error"
                    sx={{ fontSize: '0.6875rem' }}
                >
                    End date must be after start date
                </Typography>
            )}
        </Stack>
    )
}
